/**
 * @description Adiciona eventos a um elemento HTML com base nas opções fornecidas.
 *
 * @param {HTMLElement} elem - O elemento HTML ao qual os eventos serão adicionados.
 * @param {Object} options - Um objeto contendo as funções de callback para os eventos.
 * @param {Function} [options.blur] - Callback para o evento 'blur'.
 * @param {Function} [options.change] - Callback para o evento 'change'.
 * @param {Function|string} [options.click] - Callback para o evento 'click'. Se for uma string, ela será avaliada como uma função.
 * @param {Function} [options.dblclick] - Callback para o evento 'dblclick'.
 * @param {Function} [options.focus] - Callback para o evento 'focus'.
 * @param {Function|Array} [options.hover] - Callback para o evento 'mouseover' ou um array de duas funções para 'mouseover' e 'mouseout'.
 * @param {Function} [options.input] - Callback para o evento 'input'.
 * @param {Function} [options.keydown] - Callback para o evento 'keydown'.
 * @param {Function} [options.keypress] - Callback para o evento 'keypress'.
 * @param {Function} [options.keyup] - Callback para o evento 'keyup'.
 * @param {Function} [options.mousedown] - Callback para o evento 'mousedown'.
 * @param {Function} [options.mousemove] - Callback para o evento 'mousemove'.
 * @param {Function} [options.mouseover] - Callback para o evento 'mouseover'.
 * @param {Function} [options.mouseout] - Callback para o evento 'mouseout'.
 * @param {Function} [options.mouseup] - Callback para o evento 'mouseup'.
 * @param {Function} [options.load] - Callback para o evento 'load'.
 * @param {Function} [options.unload] - Callback para o evento 'unload'.
 * @param {Function} [options.select] - Callback para o evento 'select'.
 * @param {string|string[]} [options.dependsOn] - Campos que limpam e recarregam um combo remoto dependente quando mudam.
 * @returns {HTMLElement} - O elemento HTML com os eventos adicionados.
 *
 * @author Wellington Pereira
 * @since 08/08/2024
 * @version 2.0.0
 */

/**
 * Liga o comportamento de tooltip (mouseenter/mouseleave) a um elemento.
 *
 * Extraído de `options.tooltip` para ser reaproveitado por outros gatilhos,
 * como o ícone do Helper (`options.helper`) em {@link module:components/create/fieldbox}.
 *
 * @param {HTMLElement} elem - Elemento que dispara o tooltip.
 * @param {string} content - HTML exibido dentro do tooltip.
 * @returns {HTMLElement} O próprio elemento, para encadeamento.
 */
export function attachTooltip(elem, content) {
  // Tooltip atualmente aberto DESTE elemento. O fechamento é por referência (nunca `document.querySelector(".tooltip")`, que removia o tooltip de outro elemento) e não depende só de
  // `mouseleave`: se o elemento sai do DOM com o mouse em cima (ex.: o botão Fechar de um dialog), o `mouseleave` nunca dispara e o tooltip ficava preso na tela.
  let active = null;
  const dismiss = () => {
    if (!active) return;
    active.tooltip.remove();
    active.stop();
    active = null;
  };

  elem.addEventListener("mouseenter", function () {
    dismiss();
    // Cria o tooltip
    const tooltipContent = document.createElement("div");
    tooltipContent.className = "tooltip-content";
    tooltipContent.innerHTML = content;

    const tooltip = document.createElement("div");
    tooltip.className = "tooltip";
    tooltip.append(tooltipContent);

    // Adiciona o tooltip ao body para controle de posicionamento
    document.body.appendChild(tooltip);

    // Identifica se há um parent com a classe 'input-wrapper'
    const parent = elem.closest(".input-wrapper") || elem;

    // Calcula a posição do elemento ou do parent
    const rect = parent.getBoundingClientRect();
    const tooltipHeight = tooltip.offsetHeight;
    const tooltipWidth = tooltip.offsetWidth;

    let top, left, positionClass;

    // Função para verificar se o tooltip cabe na posição desejada
    function setPosition(t, l, classname) {
      const viewportHeight = window.innerHeight; // Altura da viewport
      const viewportWidth = window.innerWidth; // Largura da viewport
      const scrollTop = window.scrollY; // Posição do scroll vertical
      const scrollLeft = window.scrollX; // Posição do scroll horizontal

      // Calcular a posição inferior e direita do tooltip
      const tooltipBottom = t + tooltipHeight; // Posição inferior do tooltip
      const tooltipRight = l + tooltipWidth; // Posição direita do tooltip

      // Verifica se o tooltip cabe na altura e largura da viewport
      const fitsVertically =
        t >= scrollTop && tooltipBottom <= scrollTop + viewportHeight;
      const fitsHorizontally =
        l >= scrollLeft && tooltipRight <= scrollLeft + viewportWidth;

      // Caso o tooltip não caiba verticalmente, tenta reposicionar
      if (!fitsVertically) {
        // Se a parte inferior do tooltip estiver abaixo da viewport
        if (tooltipBottom > scrollTop + viewportHeight) {
          // Tente reposicionar o tooltip abaixo do campo
          t = rect.bottom + 2 + window.scrollY; // Ajusta para abaixo do elemento
        } else if (t < scrollTop) {
          // Se a parte superior do tooltip estiver acima da viewport
          t = rect.top + window.scrollY; // Ajusta para acima do elemento
        }
      }

      // Caso o tooltip não caiba horizontalmente, tenta reposicionar
      if (!fitsHorizontally) {
        // Se a parte direita do tooltip estiver fora da viewport
        if (tooltipRight > scrollLeft + viewportWidth) {
          // Ajusta para a esquerda
          l = rect.left - tooltipWidth - 2 + window.scrollX; // Ajusta para a esquerda do elemento
        } else if (l < scrollLeft) {
          // Se a parte esquerda do tooltip estiver fora da viewport
          l = rect.right + 2 + window.scrollX; // Ajusta para a direita do elemento
        }
      }

      // Verificação final das posições
      if (fitsVertically && fitsHorizontally) {
        top = t;
        left = l;
        positionClass = classname;
        return true;
      }

      return false;
    }

    // Prioridades de posicionamento
    const positions = [
      {
        // Direita
        top: rect.top + window.scrollY + rect.height / 2 - tooltipHeight / 2,
        left: rect.right + 8 + window.scrollX,
        class: "tooltip-right",
      },
      {
        // Esquerda
        top: rect.top + window.scrollY + rect.height / 2 - tooltipHeight / 2,
        left: rect.left - tooltipWidth - 8 + window.scrollX,
        class: "tooltip-left",
      },
      {
        // Topo
        top: rect.top - tooltipHeight - 10 + window.scrollY,
        left: rect.left + rect.width / 2 - tooltipWidth / 2 + window.scrollX,
        class: "tooltip-top",
      },
      {
        // Inferior
        top: rect.bottom + 10 + window.scrollY,
        left: rect.left + rect.width / 2 - tooltipWidth / 2 + window.scrollX,
        class: "tooltip-bottom",
      },
    ];

    // Itera pelas posições até encontrar uma que caiba na tela
    for (const pos of positions) {
      if (setPosition(pos.top, pos.left, pos.class)) {
        break;
      }
    }

    if (!positionClass) {
      const fallback = positions[0];
      top = fallback.top;
      left = fallback.left;
      positionClass = fallback.class;
    }

    // Aplica o posicionamento final
    tooltip.classList.add(positionClass);
    tooltip.style.position = "absolute";
    tooltip.style.top = `${top}px`;
    tooltip.style.left = `${left}px`;

    // Mostra o tooltip com suavidade
    setTimeout(() => {
      tooltip.classList.add("show"); // Adiciona a classe para mostrar com suavidade
    }, 10); // Pequeno delay para permitir a transição de aparecer

    // Fecha também quando o elemento deixa o DOM, ao clicar/pressionar tecla ou ao rolar: o gatilho já não está sob o mouse.
    const observer = new MutationObserver(() => {
      if (!elem.isConnected) dismiss();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    const onInteraction = () => dismiss();
    document.addEventListener("pointerdown", onInteraction, true);
    document.addEventListener("keydown", onInteraction, true);
    document.addEventListener("scroll", onInteraction, true);
    active = {
      tooltip,
      stop: () => {
        observer.disconnect();
        document.removeEventListener("pointerdown", onInteraction, true);
        document.removeEventListener("keydown", onInteraction, true);
        document.removeEventListener("scroll", onInteraction, true);
      },
    };
  });

  elem.addEventListener("mouseleave", dismiss);

  return elem;
}

export default (elem, options) => {
  const eventHandler = (callback) => (event) => {
    const detail = event?.detail;
    if (detail && typeof detail === "object" && Object.prototype.hasOwnProperty.call(detail, "previousValue")) {
      const comboEvent = detail?.hidden
        ? { ...event, target: detail.hidden, currentTarget: detail.combo || event.currentTarget }
        : event;
      callback(comboEvent, detail.previousValue, detail.nextValue);
      return;
    }

    callback(event);
  };

  const normalizeDependencyKeys = (value) => {
    const list = Array.isArray(value) ? value : [value];
    return Array.from(new Set(
      list
        .map((item) => String(item ?? '').trim())
        .filter(Boolean)
    ));
  };

  const getDependencyScope = () => (
    elem?.closest?.('.dialog')
    || elem?.closest?.('form')
    || elem?.closest?.('fieldset')
    || elem?.closest?.('main')
    || document
  );

  const findDependencyField = (scope, key) => {
    if (!scope?.querySelectorAll || !key) return null;

    const candidates = scope.querySelectorAll('.combo, input[name], select[name], textarea[name]');
    for (const candidate of candidates) {
      const id = String(candidate?.id || '').trim();
      const name = String(candidate?.name || '').trim();
      const comboId = String(candidate?.dataset?.comboId || '').trim();
      const comboName = String(candidate?.querySelector?.('input[type="hidden"][name]')?.name || '').trim();

      if ([id, name, comboId, comboName].includes(key)) {
        return candidate.classList?.contains('combo')
          ? candidate
          : candidate.closest?.('.combo') || candidate;
      }
    }

    return null;
  };

  const readConditionalValue = (scope, key) => {
    const field = findDependencyField(scope, key);
    if (!field) return undefined;

    if (field.classList?.contains('combo')) {
      return field.querySelector('input[type="hidden"]')?.value ?? '';
    }

    if (field.type === 'checkbox') {
      return field.checked ? (field.value === 'on' ? true : field.value) : false;
    }

    if (field.type === 'radio') {
      const name = String(field.name || '').trim();
      if (!name) return field.checked ? field.value : '';
      const selected = scope.querySelector(`input[type="radio"][name="${name}"]:checked`);
      return selected?.value ?? '';
    }

    return field.value;
  };

  const conditionalValueEquals = (current, expected) => (
    String(current ?? '') === String(expected ?? '')
  );

  const evaluateVisibleWhen = (condition, scope) => {
    if (!condition || typeof condition !== 'object') return true;

    const field = String(condition.field || '').trim();
    if (!field) return true;

    const current = readConditionalValue(scope, field);

    if (Array.isArray(condition.in)) {
      return condition.in.some((value) => conditionalValueEquals(current, value));
    }

    if (Array.isArray(condition.notIn)) {
      return !condition.notIn.some((value) => conditionalValueEquals(current, value));
    }

    if (Object.prototype.hasOwnProperty.call(condition, 'notEquals')) {
      return !conditionalValueEquals(current, condition.notEquals);
    }

    const expected = Object.prototype.hasOwnProperty.call(condition, 'equals')
      ? condition.equals
      : condition.value;

    return conditionalValueEquals(current, expected);
  };

  const bindConditionalVisibility = () => {
    const condition = options?.visibleWhen;
    if (!condition || typeof condition !== 'object') return;

    const fieldBox = elem?.closest?.('.fieldbox');
    if (!fieldBox) return;

    const scope = getDependencyScope();
    const preserveSpace = options?.preserveSpace === true;

    const apply = () => {
      const visible = evaluateVisibleWhen(condition, scope);
      const controls = fieldBox.querySelectorAll('input, select, textarea, button');

      if (preserveSpace) {
        fieldBox.style.visibility = visible ? 'visible' : 'hidden';
      } else {
        fieldBox.style.display = visible ? '' : 'none';
      }

      fieldBox.setAttribute('aria-hidden', String(!visible));

      if (!visible) {
        elem?.combo?.closeOptions?.();
      }

      controls.forEach((control) => {
        if (!visible) {
          if (!control.disabled) {
            control.dataset.epConditionalRestoreDisabled = '1';
            control.disabled = true;
          }
          return;
        }

        if (control.dataset.epConditionalRestoreDisabled === '1') {
          control.disabled = false;
          delete control.dataset.epConditionalRestoreDisabled;
        }
      });
    };

    scope.addEventListener('input', apply);
    scope.addEventListener('change', apply);

    queueMicrotask(apply);
    requestAnimationFrame(apply);
  };

  const bindComboDependencies = () => {
    const dependencies = normalizeDependencyKeys(options?.dependsOn);
    const combo = elem?.combo;

    if (dependencies.length === 0 || !combo || typeof combo.resolveOptions !== 'function') {
      return;
    }

    const ownKeys = new Set([
      String(elem?.id || '').trim(),
      String(elem?.dataset?.comboId || '').trim(),
      String(combo?.hidden?.name || '').trim(),
    ].filter(Boolean));

    const scope = getDependencyScope();
    const refresh = async () => {
      const currentCombo = elem?.combo;
      if (!currentCombo || typeof currentCombo.resolveOptions !== 'function') return;

      const refreshVersion = Number(elem.__comboDependencyRefreshVersion || 0) + 1;
      elem.__comboDependencyRefreshVersion = refreshVersion;

      const previousValue = currentCombo.hidden?.value ?? '';
      if (currentCombo.hidden) currentCombo.hidden.value = '';
      if (currentCombo.options && typeof currentCombo.options === 'object') {
        currentCombo.options.value = null;
      }

      currentCombo.renderData?.([], null);
      currentCombo.emitValueEvents?.(previousValue, '');

      try {
        await Promise.resolve();
        const next = await currentCombo.resolveOptions();
        if (elem.__comboDependencyRefreshVersion !== refreshVersion) return;
        currentCombo.renderData?.(Array.isArray(next) ? next : [], null);
      } catch (error) {
        if (elem.__comboDependencyRefreshVersion !== refreshVersion) return;
        currentCombo.renderData?.([], null);
        console.warn('[combo] Falha ao atualizar dependência.', {
          id: elem?.id || null,
          dependsOn: dependencies,
          message: error?.message || String(error),
        });
      }
    };

    for (const key of dependencies) {
      if (ownKeys.has(key)) continue;
      const dependency = findDependencyField(scope, key);
      dependency?.addEventListener?.('change', refresh);
    }
  };

  bindComboDependencies();
  bindConditionalVisibility();

  if (options.blur !== undefined && typeof options.blur === "function") {
    elem.addEventListener("blur", options.blur);
  }

  if (options.change !== undefined && typeof options.change === "function") {
    elem.addEventListener("change", eventHandler(options.change));
  }

        // console.log(elem, options.click);
  if (options.click !== undefined) {
    if (typeof options.click === "function") {
      if (elem.dataset.cb) {
        const click = options.click;
        delete options.click;
        const opts =
          document
            .querySelector(`[data-id="${elem.dataset.opts}"]`)
            ?.querySelectorAll("div") || [];
        opts.forEach((opt) => {
          opt.value = opt.dataset.value;
          opt.addEventListener("click", click);
        });
      } else {
        elem.addEventListener("click", options.click);
      }
    }
  }

  if (
    options.dblclick !== undefined &&
    typeof options.dblclick === "function"
  ) {
    elem.addEventListener("dblclick", options.dblclick);
  }

  if (options.focus !== undefined && typeof options.focus === "function") {
    elem.addEventListener("focus", options.focus);
  }

  if (options.hover !== undefined) {
    if (typeof options.hover === "function") {
      elem.addEventListener("mouseover", options.hover);
    } else if (typeof options.hover === "elemect" && options.hover.length > 0) {
      if (
        options.hover.length === 1 &&
        typeof options.hover[0] === "function"
      ) {
        elem.addEventListener("mouseover", options.hover[0]);
      } else if (
        options.hover.length === 2 &&
        typeof options.hover[0] === "function" &&
        typeof options.hover[1] === "function"
      ) {
        elem.addEventListener("mouseover", options.hover[0]);
        elem.addEventListener("mouseout", options.hover[1]);
      }
    }
  }

  if (options.input !== undefined && typeof options.input === "function") {
    elem.addEventListener("input", eventHandler(options.input));
  }

  if (options.keydown !== undefined && typeof options.keydown === "function") {
    elem.addEventListener("keydown", options.keydown);
  }

  if (
    options.keypress !== undefined &&
    typeof options.keypress === "function"
  ) {
    elem.addEventListener("keypress", options.keypress);
  }

  if (options.keyup !== undefined && typeof options.keyup === "function") {
    elem.addEventListener("keyup", options.keyup);
  }

  if (
    options.mousedown !== undefined &&
    typeof options.mousedown === "function"
  ) {
    elem.addEventListener("mousedown", options.mousedown);
  }

  if (
    options.mousemove !== undefined &&
    typeof options.mousemove === "function"
  ) {
    elem.addEventListener("mousemove", options.mousemove);
  }

  if (
    options.mouseover !== undefined &&
    typeof options.mouseover === "function"
  ) {
    elem.addEventListener("mouseover", options.mouseover);
  }

  if (
    options.mouseout !== undefined &&
    typeof options.mouseout === "function"
  ) {
    elem.addEventListener("mouseout", options.mouseout);
  }

  if (options.mouseup !== undefined && typeof options.mouseup === "function") {
    elem.addEventListener("mouseup", options.mouseup);
  }

  if (options.load !== undefined && typeof options.load === "function") {
    elem.addEventListener("load", options.load);
  }

  if (options.unload !== undefined && typeof options.unload === "function") {
    elem.addEventListener("unload", options.unload);
  }

  if (options.select !== undefined && typeof options.select === "function") {
    elem.addEventListener("select", options.select);
  }

  // Só texto/HTML vira tooltip de elemento. Objeto ou booleano em `tooltip` é
  // configuração própria do componente (ex.: Chart) e não pode virar conteúdo.
  if (typeof options.tooltip === "string") {
    attachTooltip(elem, options.tooltip);
  }

  return elem;
};
